import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, LogIn, LogOut, User, Package } from "lucide-react";
import { Logo, CartBadge } from "./Brand";
import LangCurrencySwitcher from "./LangCurrencySwitcher";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const Header = ({ onLogin }) => {
    const { t } = useI18n();
    const { user, isAuthed, logout } = useAuth();
    const { count, setOpen: setCartOpen } = useCart();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        toast.success(t("toast_logged_out"));
    };

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-b border-neutral-200"
        >
            <div className="max-w-[1320px] mx-auto px-4 sm:px-5 md:px-8 py-2 md:py-0 md:h-20 flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                {/* Row 1 on mobile = just the logo; on md+ same row as controls */}
                <div className="flex items-center justify-between md:justify-start">
                    <Logo />
                </div>

                {/* Row 2 on mobile = lang + auth + cart, right-aligned so the cart
                    stays in the user's familiar top-right area. */}
                <div className="flex items-center justify-end flex-wrap gap-2 md:gap-3">
                    <LangCurrencySwitcher />

                    {isAuthed ? (
                        <div className="relative">
                            <button
                                data-testid="user-menu-toggle"
                                onClick={() => setMenuOpen((v) => !v)}
                                className="h-10 px-3 rounded-full bg-white border border-neutral-200 hover:border-[#FF5722] flex items-center gap-2 transition-all"
                            >
                                <User className="h-4 w-4" />
                                <span className="hidden md:inline text-sm font-semibold">
                                    {user?.name || user?.phone || "Account"}
                                </span>
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-neutral-200 p-2 z-50">
                                    <div className="px-3 py-2 border-b border-neutral-100">
                                        {user?.name && (
                                            <div className="text-sm font-semibold text-neutral-900 truncate">
                                                {user.name}
                                            </div>
                                        )}
                                        <div className="text-xs text-neutral-500 truncate">
                                            {user?.phone}
                                        </div>
                                    </div>
                                    <Link
                                        to="/account"
                                        data-testid="my-profile-link"
                                        onClick={() => setMenuOpen(false)}
                                        className="w-full mt-1 px-3 py-2.5 rounded-xl flex items-center gap-2.5 text-left hover:bg-neutral-100 transition-colors text-neutral-800 text-sm font-medium"
                                    >
                                        <Package className="h-4 w-4" />
                                        {t("nav_my_profile")}
                                    </Link>
                                    <button
                                        data-testid="logout-btn"
                                        onClick={handleLogout}
                                        className="w-full px-3 py-2.5 rounded-xl flex items-center gap-2.5 text-left hover:bg-neutral-100 transition-colors text-neutral-800 text-sm font-medium"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        {t("nav_logout")}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button
                            data-testid="header-login-btn"
                            onClick={onLogin}
                            className="h-10 px-3 md:px-4 rounded-full bg-white border border-neutral-200 hover:border-[#FF5722] hover:shadow-sm flex items-center gap-2 text-sm font-semibold transition-all"
                        >
                            <LogIn className="h-4 w-4" />
                            <span className="hidden sm:inline">{t("nav_login")}</span>
                        </button>
                    )}

                    <button
                        data-testid="cart-icon-button"
                        onClick={() => setCartOpen(true)}
                        className="relative h-10 px-3 rounded-full bg-[#FF5722] hover:bg-[#E64A19] text-white flex items-center gap-2 transition-all hover:shadow-md hover:shadow-[#FF5722]/30"
                        aria-label="Cart"
                    >
                        <ShoppingCart className="h-4 w-4" strokeWidth={2.25} />
                        <span className="text-sm font-bold" data-testid="cart-count">
                            {count}
                        </span>
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;
